import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  useToast,
  Spinner,
  Center,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Divider,
  Tag,
  Tooltip,
  Icon
} from '@chakra-ui/react'
import { FiSearch, FiUser, FiDollarSign, FiTrendingUp, FiUsers, FiCalendar } from 'react-icons/fi'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import { getSubordinateSales } from '../../services/apiService'

type TargetUser = {
  id: number
  account: string
  username: string
  email: string | null
  phone: string | null
  grade: string | null
  createdAt: string
}

type SalesByUser = {
  userId: number
  account: string
  username: string
  salesCount: number
  salesAmount: number
  lastSaleAt: string | null
}

type SalesByAmount = {
  amount: string
  count: number
}

type SalesData = {
  id: number
  userId: number
  usdtAmount: string
  createdAt: string
  user?: {
    account: string
    username: string
  }
}

type SubordinateSalesData = {
  targetUser: TargetUser
  subordinateCount: number
  totalSalesCount: number
  totalSalesAmount: string
  salesByUser: SalesByUser[]
  salesByAmount: SalesByAmount[]
  salesData: SalesData[]
}

const Subordinate = () => {
  const [account, setAccount] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SubordinateSalesData | null>(null)
  const toast = useToast()

  const handleSearch = async () => {
    if (!account.trim()) {
      toast({
        title: '오류',
        description: '계정을 입력해주세요.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }

    setLoading(true)
    try {
      const response = await getSubordinateSales(account)
      if (response.data.statusCode === 200) {
        setData(response.data.data)
      } else {
        toast({
          title: '오류',
          description: response.data.message || '데이터를 불러올 수 없습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    } catch (error) {
      console.error('Error:', error)
      toast({
        title: '오류',
        description: '서버 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatUSDT = (amount: string | number) => {
    return parseFloat(amount.toString()).toLocaleString('ko-KR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const getGradeColor = (grade: string | null) => {
    switch (grade) {
      case 'VIP':
        return 'cyan'
      case 'SECRET':
        return 'blue'
      case 'SECRET+':
        return 'red'
      default:
        return 'gray'
    }
  }

  return (
    <DefaultLayout>
      <AdminNav />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
          {/* 검색 섹션 */}
          <Card bg="dark.card" borderColor="whiteAlpha.200" borderWidth="1px">
            <CardHeader>
              <Heading size="md" color="white">산하 매출 조회</Heading>
            </CardHeader>
            <CardBody>
              <HStack spacing={4}>
                <InputGroup maxW="400px">
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color="#A0AEC0" />
                  </InputLeftElement>
                  <Input
                    placeholder="계정을 입력하세요"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    bg="dark.bg"
                    borderColor="whiteAlpha.300"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    _hover={{ borderColor: 'whiteAlpha.400' }}
                  />
                </InputGroup>
                <Button
                  colorScheme="orange" bgGradient="linear(to-r, brand.500, blue.500)" color="white" _hover={{ bgGradient: "linear(to-r, brand.400, blue.400)" }}
                  onClick={handleSearch}
                  isLoading={loading}
                  loadingText="검색중..."
                >
                  검색
                </Button>
              </HStack>
            </CardBody>
          </Card>

          {/* 로딩 상태 */}
          {loading && (
            <Center py={8}>
              <Spinner size="xl" />
            </Center>
          )}

          {/* 데이터 표시 */}
          {data && (
            <>
              {/* 대상 사용자 정보 */}
              <Card bg="dark.card" borderColor="whiteAlpha.200" borderWidth="1px">
                <CardHeader>
                  <Heading size="md" color="white">대상 사용자 정보</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    <Stat>
                      <StatLabel color="gray.400">계정</StatLabel>
                      <StatNumber fontSize="lg" color="white">{data.targetUser.account}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel color="gray.400">사용자명</StatLabel>
                      <StatNumber fontSize="lg" color="white">{data.targetUser.username}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel color="gray.400">등급</StatLabel>
                      <StatNumber fontSize="lg" color="white">
                        <Tag colorScheme={getGradeColor(data.targetUser.grade)}>
                          {data.targetUser.grade || 'BASIC'}
                        </Tag>
                      </StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel color="gray.400">이메일</StatLabel>
                      <StatNumber fontSize="lg" color="white">{data.targetUser.email || '-'}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel color="gray.400">전화번호</StatLabel>
                      <StatNumber fontSize="lg" color="white">{data.targetUser.phone || '-'}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel color="gray.400">가입일</StatLabel>
                      <StatNumber fontSize="lg" color="white">{formatDate(data.targetUser.createdAt)}</StatNumber>
                    </Stat>
                  </SimpleGrid>
                </CardBody>
              </Card>

              {/* 매출 통계 */}
              <Card bg="dark.card" borderColor="whiteAlpha.200" borderWidth="1px">
                <CardHeader>
                  <Heading size="md" color="white">매출 통계</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    <Stat>
                      <StatLabel color="gray.400">산하 사용자 수</StatLabel>
                      <StatNumber color="blue.500">{data.subordinateCount}명</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel color="gray.400">총 매출 건수</StatLabel>
                      <StatNumber color="green.500">{data.totalSalesCount}건</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel color="gray.400">총 매출 금액</StatLabel>
                      <StatNumber color="brand.500">{formatUSDT(data.totalSalesAmount)} USDT</StatNumber>
                    </Stat>
                  </SimpleGrid>
                </CardBody>
              </Card>

              {/* 사용자별 매출 */}
              {data.salesByUser.length > 0 && (
                <Card bg="dark.card" borderColor="whiteAlpha.200" borderWidth="1px">
                  <CardHeader>
                    <Heading size="md" color="white">사용자별 매출</Heading>
                  </CardHeader>
                  <CardBody>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>계정</Th>
                          <Th>사용자명</Th>
                          <Th isNumeric>매출 건수</Th>
                          <Th isNumeric>매출 금액</Th>
                          <Th>최근 매출</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.salesByUser.map((user) => (
                          <Tr key={user.userId}>
                            <Td>{user.account}</Td>
                            <Td>{user.username}</Td>
                            <Td isNumeric>
                              <Badge colorScheme="orange" bgGradient="linear(to-r, brand.500, blue.500)" color="white" _hover={{ bgGradient: "linear(to-r, brand.400, blue.400)" }}>{user.salesCount}건</Badge>
                            </Td>
                            <Td isNumeric>
                              <Text fontWeight="bold" color="green.500">
                                {formatUSDT(user.salesAmount)} USDT
                              </Text>
                            </Td>
                            <Td>
                              {user.lastSaleAt ? formatDate(user.lastSaleAt) : '-'}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}

              {/* 금액별 매출 */}
              {data.salesByAmount.length > 0 && (
                <Card bg="dark.card" borderColor="whiteAlpha.200" borderWidth="1px">
                  <CardHeader>
                    <Heading size="md" color="white">금액별 매출 현황</Heading>
                  </CardHeader>
                  <CardBody>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>매출 금액</Th>
                          <Th isNumeric>건수</Th>
                          <Th isNumeric>비율</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.salesByAmount.map((item, index) => (
                          <Tr key={index}>
                            <Td>
                              <Text fontWeight="bold" color="brand.500">
                                {formatUSDT(item.amount)} USDT
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Badge colorScheme="green">{item.count}건</Badge>
                            </Td>
                            <Td isNumeric>
                              <Text color="gray.400">
                                {((item.count / data.totalSalesCount) * 100).toFixed(1)}%
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}

              {/* 최근 매출 내역 */}
              {data.salesData.length > 0 && (
                <Card bg="dark.card" borderColor="whiteAlpha.200" borderWidth="1px">
                  <CardHeader>
                    <Heading size="md" color="white">최근 매출 내역 (최대 50건)</Heading>
                  </CardHeader>
                  <CardBody>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>계정</Th>
                          <Th>사용자명</Th>
                          <Th isNumeric>금액</Th>
                          <Th>일시</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.salesData.map((item) => (
                          <Tr key={item.id}>
                            <Td>{item.user?.account || '-'}</Td>
                            <Td>{item.user?.username || '-'}</Td>
                            <Td isNumeric>
                              <Text fontWeight="bold" color="green.500">
                                {formatUSDT(item.usdtAmount)} USDT
                              </Text>
                            </Td>
                            <Td>{formatDate(item.createdAt)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}
            </>
          )}

          {/* 데이터가 없을 때 */}
          {!loading && !data && (
            <Center py={8}>
              <VStack spacing={4}>
                <Text fontSize="lg" color="gray.500">
                  계정을 입력하고 검색해주세요.
                </Text>
              </VStack>
            </Center>
          )}
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default Subordinate
